import React, { useEffect, useState } from 'react';
import { Form, Select, Button, Typography, Tooltip, Col, Row, Spin, message } from 'antd';
import momentTimeZone from 'moment-timezone';
import { QuestionCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { createTextSchedule, getTextSchedule, updateTextSchedule } from '@/api/novi/schedule';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getSelectedAgentId } from '@/store/account/selector';

const { Text } = Typography;

interface ScheduleFormData {
	daily_limit: number;
	allowed_times_start: string;
	allowed_times_end: string;
	days: string[];
	timezone: string;
}

const defaultFormData: ScheduleFormData = {
	daily_limit: 10,
	allowed_times_start: '',
	allowed_times_end: '',
	days: [],
	timezone: '',
};

const TextSchedule = () => {
	// State for form values
	const [formData, setFormData] = useState<ScheduleFormData>(defaultFormData);
	const [initialFormData, setInitialFormData] = useState<ScheduleFormData>(defaultFormData);
	const [isFormChanged, setIsFormChanged] = useState(false);

	const selectedCampaignId = useAppSelector(getSelectedAgentId);

	const {
		data: textCampaignSchedule,
		isLoading,
		refetch,
		isRefetching,
	} = useQuery({
		queryKey: ['campaign-schedule', selectedCampaignId],
		queryFn: () => getTextSchedule(selectedCampaignId as string),
		enabled: !!selectedCampaignId,
		retry: 2,
		staleTime: 30000,
	});

	const timezones = momentTimeZone.tz.names();
	const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData((prevData) => ({
			...prevData,
			daily_limit: parseInt(e.target.value, 10),
		}));
	};

	const handleTimeZoneChange = (value: string) => {
		setFormData((prevData) => ({
			...prevData,
			timezone: value,
		}));
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleDaySelection = (days: string[]) => {
		setFormData((prevData) => ({
			...prevData,
			days,
		}));
	};

	useEffect(() => {
		if (textCampaignSchedule?.schedule) {
			// Map days array to day names
			// In the data: Monday is 1, Sunday is 0
			const dayMapping = [
				'Sunday',
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
			];
			const selectedDays = textCampaignSchedule?.schedule?.days
				? textCampaignSchedule?.schedule?.days.map((day) => dayMapping[parseInt(day)])
				: [];

			const mappedData = {
				timezone: textCampaignSchedule?.schedule?.timezone || defaultFormData.timezone,
				daily_limit: 25, // Use default since it's not in the provided data
				days: selectedDays,
				allowed_times_start:
					textCampaignSchedule?.schedule?.allowed_times_start ||
					defaultFormData.allowed_times_start,
				allowed_times_end:
					textCampaignSchedule?.schedule?.allowed_times_end || defaultFormData.allowed_times_end,
			};

			setFormData(mappedData);
			setInitialFormData(mappedData);
			setIsFormChanged(false);
			console.log(textCampaignSchedule);
		}
	}, [textCampaignSchedule]);

	const mutation = useMutation({
		mutationFn: (data: ScheduleFormData) => {
			// Map the form data structure to the API expected structure
			const dayMapping = {
				Sunday: 0,
				Monday: 1,
				Tuesday: 2,
				Wednesday: 3,
				Thursday: 4,
				Friday: 5,
				Saturday: 6,
			};

			// Set time ranges based on schedule type
			let startTime = data.allowed_times_start;
			let endTime = data.allowed_times_end;

			// Map selected days to numbers
			const days = data.days?.map((day) => dayMapping[day as keyof typeof dayMapping]) || [];

			const apiPayload = {
				daily_limit: data.daily_limit,
				allowed_times_start: startTime,
				allowed_times_end: endTime,
				days: days,
				timezone: data.timezone,
			};

			if (textCampaignSchedule?.schedule) {
				return updateTextSchedule(selectedCampaignId as string, apiPayload);
			} else {
				// Create new schedule
				return createTextSchedule({
					campaign_id: selectedCampaignId as string,
					schedule: apiPayload,
				});
			}
		},
		onSuccess: () => {
			message.success('Schedule updated successfully');
			refetch();
			setIsFormChanged(false);
		},
		onError: (error) => {
			message.error(
				`Failed to update schedule: ${error instanceof Error ? error.message : 'Unknown error'}`,
			);
		},
	});

	useEffect(() => {
		const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialFormData);
		setIsFormChanged(hasChanged);
	}, [formData, initialFormData]);

	const handleSaveChanges = () => {
		if (selectedCampaignId) {
			mutation.mutate(formData);
		} else {
			message.error('No campaign selected');
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64">
				<Spin size="large" tip="Loading schedule settings..." />
			</div>
		);
	}

	return (
		<Form layout="vertical">
			<div className="mb-6">
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Select Days{' '}
					<Tooltip title="Days when texts campaigns are allowed">
						<QuestionCircleOutlined />
					</Tooltip>
				</label>
				<Select
					mode="multiple"
					allowClear
					placeholder="Please select"
					value={formData.days}
					onChange={handleDaySelection}
					className="w-full"
					options={daysOfWeek.map((day) => ({ label: day, value: day }))}
				/>

				{/* Start and End Time */}
				<Row gutter={8}>
					<Col lg={12} sm={24}>
						<div className="mt-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								Start Time{' '}
								<Tooltip title="Daily text campaign start time">
									<QuestionCircleOutlined />
								</Tooltip>
							</label>
							<input
								type="time"
								name="allowed_times_start"
								value={formData.allowed_times_start}
								onChange={handleInputChange}
								className="w-full p-2 border border-gray-300 rounded-md"
							/>
						</div>
					</Col>
					<Col lg={12} sm={24}>
						<div className="mt-4">
							<label className="block text-sm font-medium text-gray-700 mb-2">
								End Time{' '}
								<Tooltip title="Daily text campaign end time">
									<QuestionCircleOutlined />
								</Tooltip>
							</label>
							<input
								type="time"
								name="allowed_times_end"
								value={formData.allowed_times_end}
								onChange={handleInputChange}
								className="w-full p-2 border border-gray-300 rounded-md"
							/>
						</div>
					</Col>
				</Row>
			</div>
			<div className="mb-6">
				<label className="block text-sm font-medium text-gray-700 mb-2">Time Zone</label>

				<Select
					showSearch
					value={formData.timezone}
					placeholder="Select a time zone"
					style={{ width: '100%' }}
					onChange={handleTimeZoneChange}
					options={timezones.map((zone) => ({ label: zone, value: zone }))}
					filterOption={(input, option) =>
						(option?.label as string).toLowerCase().includes(input.toLowerCase())
					}
				/>
			</div>

			<div className="mb-6">
				<label className="text-sm font-medium text-gray-700 mb-2">
					Daily Message Limit: <span className="text-green-600">{formData.daily_limit}</span>
				</label>
				<input
					type="range"
					min="10"
					max="1000"
					step="1"
					value={formData.daily_limit}
					onChange={handleSliderChange}
					className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer mt-5"
				/>
				<div className="flex justify-between text-xs text-gray-500 mt-1">
					<span>10 texts</span>
					<span>1000 texts</span>
				</div>
				<Text type="secondary" style={{ display: 'block', marginTop: '5px', fontSize: '12px' }}>
					This limits the number of messages sent per day to avoid overwhelming your leads or
					hitting carrier limits.
				</Text>
			</div>

			<div className="text-end">
				<Button
					icon={<SaveOutlined />}
					type="primary"
					size="large"
					disabled={!isFormChanged || mutation.isPending || isRefetching}
					onClick={handleSaveChanges}
					loading={mutation.isPending}
				>
					{mutation.isPending ? 'Saving...' : 'Save Changes'}
				</Button>
			</div>
		</Form>
	);
};

export default TextSchedule;
